import React from 'react';
import { Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAskSwirlStore } from '../../stores/askSwirlStore';
import { useSmartCards } from '../../hooks/useSmartCards';

export default function AskSwirlEmptyState({ 
  userName = 'there'
}) {
  const navigate = useNavigate();
  const { addChip, setPrefilledInput, clearChips } = useAskSwirlStore();
  const { cards, loading } = useSmartCards();

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // Cards are now in the unified schema format from useSmartCards
  // Each card has: id, type, title, description, ctaLabel, ctaAction, gradient, icon
  const focusCards = cards || [];

  // Debug: Log final cards array
  console.log('ASK SWIRL FINAL CARDS:', focusCards);
  console.log('CARD COUNT:', focusCards.length);

  return (
    <div className="flex-1 flex items-start justify-center pt-12 sm:pt-20 lg:pt-32 px-4 sm:px-6 lg:px-8 pb-8 relative z-10">
      <div className="max-w-5xl w-full">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-10 lg:mb-12">
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-2 sm:mb-3">
            <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
              {getGreeting()}, {userName}
            </span>
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            What do you want to work on right now
          </p>
        </div>

        {/* Focus Cards */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 mb-6 sm:mb-8">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="bg-white/60 backdrop-blur-lg rounded-2xl p-5 sm:p-6 border border-white/50 shadow-lg animate-pulse h-full"
              >
                <div className="h-3 sm:h-4 bg-gray-200 rounded w-24 mb-2"></div>
                <div className="h-5 sm:h-6 bg-gray-200 rounded w-32 mb-2 sm:mb-3"></div>
                <div className="h-16 sm:h-20 bg-gray-200 rounded mb-4 sm:mb-5"></div>
                <div className="h-4 sm:h-5 bg-gray-200 rounded w-28"></div>
              </div>
            ))}
          </div>
        ) : focusCards.length === 0 ? (
          // Case A: 0 cards - Centered empty state
          <div className="flex flex-col items-center justify-center py-12">
            <div className="mb-4 opacity-40">
              <Sparkles size={48} className="text-purple-400" />
            </div>
            <p className="text-base text-gray-500 mb-4">No active focuses yet</p>
            <button
              onClick={() => navigate('/swirlspace')}
              className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-medium rounded-lg hover:opacity-90 transition-opacity shadow-lg"
            >
              Start a focus
            </button>
          </div>
        ) : focusCards.length === 1 ? (
          // Case B: 1 card - Center it horizontally
          <div className="flex justify-center mb-6 sm:mb-8">
            <div className="w-full max-w-sm">
              {focusCards.map((card) => {
                return (
                  <div
                    key={card.id}
                    className="group relative bg-white/60 backdrop-blur-lg rounded-2xl p-5 sm:p-6 border border-white/50 shadow-lg hover:shadow-2xl transition-all duration-200 hover:scale-[1.01] hover:-translate-y-1 h-full flex flex-col"
                  >
                    {/* Gradient Border Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-200`}></div>
                    
                    <div className="relative flex-1 flex flex-col">
                      {/* Category */}
                      <div className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        {card.type === 'active-focus' && 'ACTIVE FOCUS'}
                        {card.type === 'focus-suggestion' && 'FOCUS SUGGESTION'}
                        {card.type === 'insight' && 'INSIGHTS'}
                      </div>

                      {/* Title */}
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3">
                        {card.title}
                      </h3>

                      {/* Description */}
                      <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-5 leading-relaxed flex-1">
                        {card.description}
                      </p>

                      {/* Action Button */}
                      <button 
                        onClick={card.ctaAction}
                        className={`flex items-center gap-1.5 text-xs sm:text-sm font-medium bg-gradient-to-r ${card.gradient} bg-clip-text text-transparent hover:opacity-90 transition-all duration-200 group cursor-pointer min-h-[44px] sm:min-h-[auto]`}
                      >
                        {card.icon}
                        <span className="group-hover:underline decoration-2 underline-offset-2">{card.ctaLabel}</span>
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500 group-hover:text-purple-600 transition-all duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>

                      {/* Icon decoration */}
                      <div className={`absolute -top-2 -right-2 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br ${card.gradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-200`}></div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : focusCards.length === 2 ? (
          // Case C: 2 cards - Center them as a pair
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 sm:gap-6 mb-6 sm:mb-8">
            {focusCards.map((card) => {
              return (
                <div
                  key={card.id}
                  className="group relative bg-white/60 backdrop-blur-lg rounded-2xl p-5 sm:p-6 border border-white/50 shadow-lg hover:shadow-2xl transition-all duration-200 hover:scale-[1.01] hover:-translate-y-1 w-full max-w-sm h-full flex flex-col"
                >
                  {/* Gradient Border Effect */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-200`}></div>
                  
                  <div className="relative flex-1 flex flex-col">
                    {/* Category */}
                    <div className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                      {card.type === 'active-focus' && 'ACTIVE FOCUS'}
                      {card.type === 'focus-suggestion' && 'FOCUS SUGGESTION'}
                      {card.type === 'insight' && 'INSIGHTS'}
                    </div>

                    {/* Title */}
                    <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3">
                      {card.title}
                    </h3>

                    {/* Description */}
                    <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-5 leading-relaxed flex-1">
                      {card.description}
                    </p>

                    {/* Action Button */}
                    <button 
                      onClick={card.ctaAction}
                      className={`flex items-center gap-1.5 text-xs sm:text-sm font-medium bg-gradient-to-r ${card.gradient} bg-clip-text text-transparent hover:opacity-90 transition-all duration-200 group cursor-pointer min-h-[44px] sm:min-h-[auto]`}
                    >
                      {card.icon}
                      <span className="group-hover:underline decoration-2 underline-offset-2">{card.ctaLabel}</span>
                      <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500 group-hover:text-purple-600 transition-all duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>

                    {/* Icon decoration */}
                    <div className={`absolute -top-2 -right-2 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br ${card.gradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-200`}></div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Case D: 3+ cards - Use full-width grid with equal height
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 mb-6 sm:mb-8">
            {focusCards.map((card) => {
                return (
                  <div
                    key={card.id}
                    className="group relative bg-white/60 backdrop-blur-lg rounded-2xl p-5 sm:p-6 border border-white/50 shadow-lg hover:shadow-2xl transition-all duration-200 hover:scale-[1.01] hover:-translate-y-1 h-full flex flex-col"
                  >
                    {/* Gradient Border Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-br ${card.gradient} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-200`}></div>
                    
                    <div className="relative flex-1 flex flex-col">
                      {/* Category */}
                      <div className="text-[10px] sm:text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                        {card.type === 'active-focus' && 'ACTIVE FOCUS'}
                        {card.type === 'focus-suggestion' && 'FOCUS SUGGESTION'}
                        {card.type === 'insight' && 'INSIGHTS'}
                      </div>

                      {/* Title */}
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 mb-2 sm:mb-3">
                        {card.title}
                      </h3>

                      {/* Description */}
                      <p className="text-xs sm:text-sm text-gray-600 mb-4 sm:mb-5 leading-relaxed flex-1">
                        {card.description}
                      </p>

                      {/* Action Button */}
                      <button 
                        onClick={card.ctaAction}
                        className={`flex items-center gap-1.5 text-xs sm:text-sm font-medium bg-gradient-to-r ${card.gradient} bg-clip-text text-transparent hover:opacity-90 transition-all duration-200 group cursor-pointer min-h-[44px] sm:min-h-[auto]`}
                      >
                        {card.icon}
                        <span className="group-hover:underline decoration-2 underline-offset-2">{card.ctaLabel}</span>
                        <svg className="w-3 h-3 sm:w-4 sm:h-4 text-purple-500 group-hover:text-purple-600 transition-all duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>

                      {/* Icon decoration */}
                      <div className={`absolute -top-2 -right-2 w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br ${card.gradient} rounded-full opacity-10 group-hover:opacity-20 transition-opacity duration-200`}></div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Stars decoration underneath cards */}
        {focusCards.length > 0 && (
          <div className="flex justify-center items-center gap-2 sm:gap-3 mt-6 sm:mt-8">
            {[
              { size: 'text-[10px] sm:text-[12px]' },
              { size: 'text-[8px] sm:text-[10px]' },
              { size: 'text-[12px] sm:text-[14px]' },
              { size: 'text-[9px] sm:text-[11px]' },
              { size: 'text-[14px] sm:text-[16px]' },
              { size: 'text-[10px] sm:text-[12px]' },
              { size: 'text-[8px] sm:text-[10px]' },
              { size: 'text-[11px] sm:text-[13px]' },
              { size: 'text-[9px] sm:text-[11px]' },
              { size: 'text-[12px] sm:text-[14px]' },
              { size: 'text-[8px] sm:text-[10px]' }
            ].map((star, i) => (
              <svg
                key={i}
                className={`${star.size} text-purple-400 opacity-70 animate-twinkle`}
                style={{ 
                  animationDuration: `${1.5 + i * 0.3}s`,
                  animationDelay: `${i * 0.15}s`,
                  width: '1em',
                  height: '1em'
                }}
                viewBox="0 0 24 24"
                fill="currentColor"
              >
                <path d="M12 0L14.5 9.5L24 12L14.5 14.5L12 24L9.5 14.5L0 12L9.5 9.5L12 0Z" />
              </svg>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
